import * as React from "react"
import styled, { css } from "styled-components"
import { compose, withStateHandlers, StateHandlerMap } from "recompose"
import { navigate } from "gatsby"
import { InstantSearch } from "react-instantsearch-dom"
import {
  IconArrowRight,
  IconSearch,
  Link,
  StandardButton,
  Variables
} from "@life-without-barriers/react-components"
import { TopicHit } from "@life-without-barriers/gatsby-common"

import {
  connectQueryAndHits,
  QueryAndHitsProps
} from "../../lib/connectQueryAndHits"

import NoSearchResults from "./Search/NoSearchResults"

const { muli, black, youthPinkXXXLight, youthPinkMedium, greyXXXLight, white } =
  Variables

const placeholderStyles = `
  color: #9B9B9B;
  font-family: ${muli};
`

const StyledSearchInput = styled.input`
  -webkit-appearance: none;
  padding-right: 3rem;
  line-height: 2rem;

  /** ie11 height */
  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    height: 50px;
  }

  ::placeholder {
    ${placeholderStyles};
  }

  /* IE hack to hide clear 'x' to thegit add right of input */
  &::-ms-clear {
    height: 0;
    width: 0;
  }
`

export const StyledAllResults = styled.li`
  cursor: pointer;
  color: ${youthPinkMedium};
  transition: color ease 0.3s, background-color ease 0.3s;
  background-color: ${greyXXXLight};
  path {
    fill: ${youthPinkMedium};
    transition: color ease 0.3s;
  }

  &:hover {
    background-color: ${youthPinkMedium};
    color: ${white};
    path {
      fill: ${white};
    }
  }
`

const StyledIconSearch = styled(IconSearch)`
  top: 50%;
  transform: translateY(-50%);
`

// Need to exclude selected so it's not passed down to the dom node (React will complain)

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
const StyledTopicLink = styled(({ selected, ...props }: any) => (
  <Link {...props} />
))`
  ${(props: { selected: boolean }) =>
    props.selected &&
    css`
      background-color: ${youthPinkXXXLight};

      &:hover {
        color: #000;
      }
    `}
`

const Hit = ({
  hit,
  selected,
  setSelected,
  resetSelected
}: {
  hit: TopicHit
  selected: boolean
  setSelected: () => void
  resetSelected: () => void
}) => (
  <li
    key={hit.objectID}
    className="tl"
    onMouseEnter={setSelected}
    onMouseLeave={() => resetSelected()}
  >
    <StyledTopicLink
      to={`/foster-care/support/carer-guide/topic/${hit.slug}`}
      title={hit.title}
      className="db pa3 no-underline normal br2 b black"
      selected={selected}
    >
      {hit.title}
      <span className="normal">
        {hit.category && ` - ${hit.category[0].categoryTitle}`}
      </span>
    </StyledTopicLink>
  </li>
)

const Hits = ({
  selectedIndex,
  setSelected,
  hits,
  setHitsFocused,
  resetSelected,
  onSearch
}: {
  selectedIndex: number
  setSelected: (index: number) => void
  hits: TopicHit[]
  setHitsFocused: (input: boolean) => void
  resetSelected: () => void
  onSearch: () => void
}) => {
  return (
    <ul
      className="ba b-lwb-grey-dark br2-ns bg-lwb-white absolute w-100 mt1 list pa2"
      onMouseEnter={() => setHitsFocused(true)}
      onMouseLeave={() => setHitsFocused(false)}
    >
      {hits.map((hit: TopicHit, index: number) => (
        <Hit
          hit={hit}
          key={hit.objectID}
          selected={selectedIndex === index}
          setSelected={() => setSelected(index)}
          resetSelected={resetSelected}
        />
      ))}
      <StyledAllResults
        className="pa3 no-underline b br2 mt2 w-100 mw-100 tc ttn"
        onClick={onSearch}
      >
        View all results
        <IconArrowRight className="ml2" height="10" width="6" />
      </StyledAllResults>
    </ul>
  )
}

const onEnterAction = ({
  selectedIndex,
  hits,
  query,
  setShowHits,
  onSearch
}: Props) => {
  if (selectedIndex >= 0) {
    const { slug } = hits[selectedIndex]
    void navigate(`/foster-care/support/carer-guide/topic/${slug}`)
  } else {
    setShowHits(false)
    onSearch(query)
  }
}

const handleActionKeys = (props: Props) => (event: React.KeyboardEvent) => {
  switch (event.key) {
    case "Enter":
      onEnterAction(props)
      break
    case "Escape":
      props.setShowHits(false)
      break
    case "ArrowUp":
      props.setSelected(props.selectedIndex - 1)
      event.preventDefault()
      break
    case "ArrowDown":
      props.setSelected(props.selectedIndex + 1)
      event.preventDefault()
      break
  }
}

const SearchBox = (props: Props) => (
  <div className={`flex justify-center ${props?.className || ""}`}>
    <div className="relative w-100">
      <StyledSearchInput
        className={`pv2 pl3 w-100 ba b-lwb-grey-x-light br2 outline-0 muli ${
          props.id ? `t-search-input-${props.id}` : "t-search-input"
        }`}
        type="search"
        value={props.query}
        placeholder="Search the guide"
        onBlur={() => !props.hitsFocused && props.setShowHits(false)}
        onChange={(event) => {
          props.resetSelected()
          props.refine(event.target.value)
          props.setShowHits(true)
        }}
        onKeyDown={handleActionKeys(props)}
      />
      <StyledIconSearch
        className="absolute right-1"
        height="20"
        width="20"
        color={black}
      />
      {props.showHits && props.query && props.hits.length > 0 && (
        <Hits
          selectedIndex={props.selectedIndex}
          resetSelected={props.resetSelected}
          setSelected={props.setSelected}
          setHitsFocused={props.setHitsFocused}
          onSearch={() => {
            props.onSearch(props.query)
            props.setShowHits(false)
          }}
          hits={props.hits}
        />
      )}
      {props.showHits && props.query && props.hits.length === 0 && (
        <div className="ba b-lwb-grey-light br2-ns bg-lwb-white absolute w-100 mt1 list pa2 color-lwb-black">
          <NoSearchResults classes="w-100" />
        </div>
      )}
    </div>
    {props.hideButton !== false && (
      <div className="ml4 dn db-ns">
        <StandardButton
          className="t-search-button"
          onClick={() => props.onSearch(props.query)}
        >
          Search
        </StandardButton>
      </div>
    )}
  </div>
)

export interface SearchBoxProps {
  id?: string
  initialQuery?: string
  hideButton?: boolean
  className?: string
  onSearch: (query: string) => void
}

interface State {
  selectedIndex: number
  showHits: boolean
  hitsFocused: boolean
}

interface StateHandlers extends StateHandlerMap<State> {
  resetSelected: () => State
  setSelected: (nextIndex: number) => State
  setShowHits: (showHits: boolean) => State
}

type Props = SearchBoxProps &
  QueryAndHitsProps<TopicHit> &
  State &
  StateHandlers

const initialState = () => ({
  selectedIndex: -1,
  hitsFocused: false,
  showHits: false
})

const getNextSelectedIndex = (hitCount: number, nextIndex: number) => {
  const maxIndex = hitCount - 1
  if (nextIndex > maxIndex) {
    return 0
  } else if (nextIndex < 0) {
    return maxIndex
  } else {
    return nextIndex
  }
}

const stateHandlers = {
  setSelected:
    (previous: State, { hits }: QueryAndHitsProps<TopicHit>) =>
    (nextIndex: number) => ({
      ...previous,
      selectedIndex: getNextSelectedIndex(hits.length, nextIndex)
    }),

  resetSelected: (previous: State) => () => ({
    ...previous,
    selectedIndex: -1
  }),

  setShowHits: (previous: State) => (showHits: boolean) => ({
    ...previous,
    showHits
  }),

  setHitsFocused: (previous: State) => (hitsFocused: boolean) => ({
    ...previous,
    hitsFocused
  })
}

const ConnectedSearchBox = compose<Props, SearchBoxProps>(
  connectQueryAndHits(5),
  withStateHandlers<
    State,
    StateHandlers,
    SearchBoxProps & QueryAndHitsProps<TopicHit>
  >(initialState, stateHandlers)
)(SearchBox)

const InstantSearchComponent = (props: SearchBoxProps) => (
  <InstantSearch
    appId={process.env.GATSBY_ALGOLIA_APP_ID || ""}
    apiKey={process.env.GATSBY_ALGOLIA_SEARCH_API_KEY || ""}
    indexName={process.env.GATSBY_ALGOLIA_INDEX_NAME || ""}
  >
    <ConnectedSearchBox {...props} />
  </InstantSearch>
)

export default InstantSearchComponent
